import React from 'react';

export type ButtonVariant = 'primary' | 'secondary' | 'ghost' | 'danger';
export type ButtonSize = 'sm' | 'md' | 'lg';

export interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  /**
   * Visual style variant.
   * - `primary`: High-contrast solid fill (bg-primary-fill text-white hover:bg-primary-focus)
   * - `secondary`: Bordered surface (bg-neutral border-base-300 text-neutral-content)
   * - `ghost`: Transparent with subtle hover (hover:bg-base-300/60)
   * - `danger`: Bordered destructive action (border-error text-error-soft hover:bg-error hover:text-white)
   * @default 'secondary'
   */
  variant?: ButtonVariant;

  /**
   * Control size.
   * - `sm`: 32px desktop height (min-h-[44px] on mobile viewports < md)
   * - `md`: 40px desktop height (min-h-[44px] on mobile viewports < md)
   * - `lg`: 48px height (min-h-[48px])
   * @default 'md'
   */
  size?: ButtonSize;

  /** Icon placed at inline-start (right side in Persian RTL) */
  leadingIcon?: React.ReactNode;
  /** Convenience alias for leadingIcon */
  iconLeading?: React.ReactNode;
  /** Convenience alias for leadingIcon */
  icon?: React.ReactNode;

  /** Icon placed at inline-end (left side in Persian RTL) */
  trailingIcon?: React.ReactNode;
  /** Convenience alias for trailingIcon */
  iconTrailing?: React.ReactNode;

  /** Shows an inline spinner replacing or preceding the icon and disables interaction */
  loading?: boolean;

  /** Stretches the button to full container width (used for mobile primary CTAs) */
  fullWidth?: boolean;

  /** Optional href to render the button as an anchor tag */
  href?: string;
  target?: string;
  rel?: string;
}

export const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
  (
    {
      variant = 'secondary',
      size = 'md',
      leadingIcon,
      iconLeading,
      icon,
      trailingIcon,
      iconTrailing,
      loading = false,
      fullWidth = false,
      disabled,
      children,
      className = '',
      type,
      href,
      target,
      rel,
      ...props
    },
    ref
  ) => {
    const lead = leadingIcon ?? iconLeading ?? icon;
    const trail = trailingIcon ?? iconTrailing;

    const sizeClasses: Record<ButtonSize, string> = {
      sm: 'min-h-[44px] md:min-h-8 h-11 md:h-8 px-3 text-xs',
      md: 'min-h-[44px] md:min-h-10 h-11 md:h-10 px-4 text-sm',
      lg: 'min-h-[48px] h-12 px-5 text-base'
    };

    const variantClasses: Record<ButtonVariant, string> = {
      primary:
        'bg-primary-fill text-white hover:bg-primary-focus focus-visible:ring-primary focus-ring',
      secondary:
        'bg-neutral border border-base-300 text-neutral-content hover:bg-base-300/80 hover:text-base-content focus-visible:ring-primary focus-ring',
      ghost:
        'bg-transparent text-base-content/80 hover:text-base-content hover:bg-base-300/60 focus-visible:ring-primary focus-ring',
      danger:
        'border border-error text-error-soft hover:bg-error hover:text-white focus-visible:ring-error'
    };

    const combinedClasses = `inline-flex items-center justify-center gap-2 rounded-xl font-semibold select-none transition-all duration-150 active:scale-[0.97] focus-visible:outline-none focus-visible:ring-2 ring-offset-2 ring-offset-base-100 disabled:opacity-50 disabled:cursor-not-allowed disabled:pointer-events-none ${
      sizeClasses[size]
    } ${variantClasses[variant]} ${fullWidth ? 'w-full' : ''} ${className}`;

    const content = (
      <>
        {loading && (
          <span
            className="size-4 shrink-0 animate-spin rounded-full border-2 border-current border-t-transparent"
            aria-hidden="true"
          />
        )}
        {!loading && lead}
        {children && <span>{children}</span>}
        {!loading && trail}
      </>
    );

    if (href) {
      return (
        <a
          ref={ref as unknown as React.Ref<HTMLAnchorElement>}
          href={href}
          target={target}
          rel={rel}
          role="button"
          aria-busy={loading}
          aria-disabled={disabled || loading}
          className={`${combinedClasses} ${
            disabled || loading ? 'pointer-events-none opacity-50 cursor-not-allowed' : ''
          }`}
          {...(props as unknown as React.AnchorHTMLAttributes<HTMLAnchorElement>)}
        >
          {content}
        </a>
      );
    }

    return (
      <button
        ref={ref}
        type={type || 'button'}
        disabled={disabled || loading}
        aria-busy={loading}
        className={combinedClasses}
        {...props}
      >
        {content}
      </button>
    );
  }
);

Button.displayName = 'Button';
